import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Drawer, Stack, Group, Text, Badge, CopyButton, ActionIcon, Tooltip, Code, Button, Portal, UnstyledButton } from "@mantine/core";
import { IconCopy, IconCheck, IconTrash, IconCode } from "@tabler/icons-react";
import { useApiLog, clearApiLog, useVerificationModalOpen } from "../utils/api-log";
import "./under-the-hood.css";

// Layering: the VX verification modal is a fixed overlay at z-index 9999.
// The header button and this drawer must stay reachable above it.
const Z_DRAWER = 10001;
const Z_BUTTON = 10002; // above the drawer too, so the header toggle always works

const pretty = (v) => (v == null ? "" : JSON.stringify(v, null, 2));

function CopyBlock({ title, text, tone }) {
  return (
    <Stack gap={6} className="vt-hood-block">
      <Group justify="space-between" wrap="nowrap">
        <Text size="xs" fw={600} tt="uppercase" c={tone || "dimmed"} style={{ letterSpacing: "0.08em" }}>
          {title}
        </Text>
        <CopyButton value={text} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? "Copied" : "Copy"} withArrow zIndex={Z_DRAWER + 1}>
              <ActionIcon size="sm" variant="subtle" color={copied ? "teal" : "gray"} onClick={copy} aria-label={`Copy ${title}`}>
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
      <Code block className="vt-hood-pre">{text || "—"}</Code>
    </Stack>
  );
}

function Entry({ entry }) {
  const isVx = entry.kind === "create" || entry.kind === "status";
  const requestTitle = isVx ? "Request to VX" : "Request to demo backend";
  const responseTitle = isVx ? "Response from VX" : "Response from demo backend";
  const headerLines = Object.entries(entry.headers || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
  const requestText = `${entry.method} ${entry.url}\n${headerLines}${entry.body != null ? `\n\n${pretty(entry.body)}` : ""}`;
  const pending = entry.ok === null && !entry.error;
  const responseText = pending ? "waiting…" : pretty(entry.error || entry.response);
  const statusColor = pending ? "gray" : entry.ok ? "teal" : "red";
  const when = new Date(entry.at).toLocaleTimeString([], { hour12: false });

  return (
    <Stack gap="sm" p="md" className="vt-hood-entry" style={{ border: "1px solid #e4e7e6", borderRadius: 12, background: "#fff" }}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap={8} wrap="nowrap">
          <Badge color={statusColor} variant="light" size="sm">
            {pending ? "…" : entry.status || (entry.error ? "ERR" : "")}
          </Badge>
          <Text size="sm" fw={600}>{entry.label}</Text>
        </Group>
        <Text size="xs" c="dimmed" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", whiteSpace: "nowrap" }}>
          {when}{entry.durationMs != null ? ` · ${entry.durationMs} ms` : ""}
        </Text>
      </Group>
      <CopyBlock title={requestTitle} text={requestText} />
      <CopyBlock title={responseTitle} text={responseText} tone={entry.ok === false ? "red" : undefined} />
      {entry.kind === "status" && (
        <Text size="xs" c="dimmed" style={{ borderTop: "1px dashed #e4e7e6", paddingTop: 8 }}>
          Production integrations usually receive this result by webhook instead of polling; the payload is the same JSON.
        </Text>
      )}
    </Stack>
  );
}

export default function UnderTheHoodDrawer({ opened, onClose }) {
  const entries = useApiLog();
  const modalOpen = useVerificationModalOpen();

  // While the VX modal is up Mantine's own Esc / click-outside handling is
  // off (no overlay, no focus trap), so handle both here at the capture
  // phase on the document, before the modal can swallow them. Clicks that
  // land inside the drawer, on the header toggle, or on the VX iframe itself
  // do not close it; a click on the modal's scrim or the page does.
  useEffect(() => {
    if (!opened || !modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    const onClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(".vt-hood-drawer, .vt-hood-btn, [data-vx-iframe]")) return;
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onClick, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onClick, true);
    };
  }, [opened, modalOpen, onClose]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={480}
      zIndex={Z_DRAWER}
      closeOnEscape
      closeButtonProps={{ "aria-label": "Close Under the hood", className: "vt-hood-close", size: "lg" }}
      // While the VX modal is up the drawer must not block it: no scrim, no
      // focus trap, no scroll lock, and clicking the modal does not close it.
      withOverlay={!modalOpen}
      closeOnClickOutside={!modalOpen}
      trapFocus={!modalOpen}
      lockScroll={!modalOpen}
      overlayProps={{ backgroundOpacity: 0.45, blur: 1 }}
      title={
        <Group gap="sm" wrap="nowrap">
          <Text fw={600} style={{ whiteSpace: "nowrap" }}>Under the hood</Text>
          <Badge variant="filled" color="yellow" c="#1c1300" size="sm">DEMO</Badge>
          <Badge variant="light" color="gray" style={{ whiteSpace: "nowrap" }}>last {entries.length} of 5</Badge>
        </Group>
      }
      classNames={{ content: "vt-hood-drawer", header: "vt-hood-drawer__header", title: "vt-hood-drawer__title", body: "vt-hood-drawer__body" }}
      styles={{ content: modalOpen ? { boxShadow: "-12px 0 32px rgba(0,0,0,0.25)" } : undefined }}
    >
      <div className="vt-hood-scroll">
      <Stack gap="md" className="vt-hood-stack">
        <Text size="sm" fw={500}>
          Demo only: the API traffic between this site and VX.
        </Text>
        <Text size="xs" c="dimmed">
          Your server makes these calls with your VX API key; this demo's server does the same on its behalf. Newest first; keys and tokens are never shown.
        </Text>
        {entries.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            No calls yet. Start a verification and the request and response will appear here.
          </Text>
        ) : (
          entries.map((e) => <Entry key={e.id} entry={e} />)
        )}
        {entries.length > 0 && (
          <Button variant="subtle" color="gray" size="xs" leftSection={<IconTrash size={14} />} onClick={clearApiLog} style={{ alignSelf: "flex-end" }}>
            Clear history
          </Button>
        )}
      </Stack>
      </div>
      <div className="vt-hood-footer">
        <Button fullWidth size="md" color="dark" radius="md" onClick={onClose}>
          Close
        </Button>
      </div>
    </Drawer>
  );
}

// Header control. While the VX modal is on screen the header sits under the
// modal's scrim, so a fixed copy of the button is portaled above it at the
// same spot; the in-header one stays for layout.
export function UnderTheHoodButton({ open, onToggle, compact }) {
  const modalOpen = useVerificationModalOpen();
  const ref = useRef(null);
  const [rect, setRect] = useState(null);

  // The floating copy exists only to OPEN the drawer over the modal scrim;
  // once the drawer is open it must not render (it sat on the drawer header).
  const float = modalOpen && !open;

  useLayoutEffect(() => {
    if (!float) { setRect(null); return undefined; }
    const measure = () => {
      const r = ref.current?.getBoundingClientRect();
      if (r) setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [float]);

  const inner = (
    <>
      <IconCode size={15} />
      {!compact && <span>Under the hood</span>}
      <span className="vt-hood-btn__tag">DEMO</span>
    </>
  );
  const cls = `vt-hood-btn${open ? " vt-hood-btn--open" : ""}${compact ? " vt-hood-btn--compact" : ""}`;

  return (
    <>
      <Tooltip label="Demo tooling: the API traffic between this site and VX">
        <UnstyledButton ref={ref} className={cls} aria-label="Under the hood (demo tooling)" aria-pressed={open} onClick={onToggle}>
          {inner}
        </UnstyledButton>
      </Tooltip>
      {float && rect && (
        <Portal>
          <UnstyledButton
            className={cls}
            aria-label="Under the hood (demo tooling)"
            aria-pressed={open}
            onClick={onToggle}
            style={{ position: "fixed", top: rect.top, left: rect.left, width: rect.width, height: rect.height, zIndex: Z_BUTTON }}
          >
            {inner}
          </UnstyledButton>
        </Portal>
      )}
    </>
  );
}
